import { FC, useEffect, useState, useContext } from "react";
import { ListItem } from "@mui/material";
import "@/components/navigation/navigationBar.css";
import {
  adminSubroutes,
  settingsSoubRoutes,
  trackedServiceSubRoutes,
} from "@/components/navigation/adminSubRoutes";
import { adminLmsSubRoutes } from "@/components/navigation/lmsSubRoutes";
import {
  NavigationItemButton,
  NavigationListItemContent,
  NavigationNavLink,
} from "@/components/navigation/navigationItems";
import { useTranslation } from "react-i18next";
import Restricted from "@/consumer/restricted";
import GenericNavigationCollapse from "@/components/navigation/genericNavigationCollapse";
import GenericExpandableNavigationListItem from "@/components/navigation/genericExpandableNavigationListItem";
import PermissionContext from "@/context/permissionContext";
import { COURSE_ACCESS_PERMISSION } from "@/components/lms/lms.utils";
import { useManagerLmsAccess } from "@/context/managerLmsAccessContext";

interface Props {
  drawerOpen: boolean;
  selectedIndex: number;
  handleListItemClick?: any;
  handleListSoubrouteItemClick?: any;
  subdomain: string;
  subRouteToOpen: string;
}

const AdminRoutes: FC<Props> = ({
  drawerOpen,
  subdomain,
  selectedIndex,
  handleListItemClick,
  handleListSoubrouteItemClick,
  subRouteToOpen,
}) => {
  const [subroutesOpen, setSubroutesOpen] = useState({});
  const { isAllowedTo } = useContext(PermissionContext);
  const { canAccess: canAccessLms } = useManagerLmsAccess();
  const { t } = useTranslation();

  const toggleSubrouteOpen = (routeId: any) => {
    setSubroutesOpen((prevState: any) => ({
      ...prevState,
      [routeId]: !prevState[routeId],
    }));
  };

  useEffect(() => {
    toggleSubrouteOpen(subRouteToOpen);
  }, [subRouteToOpen]);

  const hasAnyAccessibleSubRoutes = (routes: any[]) => {
    return routes.some((route) =>
      route.permission ? isAllowedTo(route.permission) : true
    );
  };

  const lmsNavigation = hasAnyAccessibleSubRoutes(adminLmsSubRoutes) && (
    <>
      <GenericExpandableNavigationListItem
        listItemIconName="lms"
        listItemLabel={t("lms_product_name")}
        drawerOpen={drawerOpen}
        handleSubrouteOpen={toggleSubrouteOpen}
        subRouteOpen={subroutesOpen}
        subRouteKey="klearning"
      />
      <GenericNavigationCollapse
        routesToRender={adminLmsSubRoutes}
        drawerOpen={drawerOpen}
        selectedIndex={selectedIndex}
        subRouteOpen={subroutesOpen}
        handleListSoubrouteItemClick={handleListSoubrouteItemClick}
        subRouteKey="klearning"
      />
    </>
  );

  const portalLmsNavigation = (
    <ListItem
      disablePadding
      className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
    >
      <NavigationNavLink to="/kaerdemy">
        <NavigationItemButton
          drawerOpen={drawerOpen}
          onClick={(event: any) => handleListItemClick(event, 6000)}
          selected={selectedIndex === 6000}
        >
          <NavigationListItemContent
            drawerOpen={drawerOpen}
            primaryText={t("lms_product_name")}
            icon="lms"
          />
        </NavigationItemButton>
      </NavigationNavLink>
    </ListItem>
  );

  if (subdomain === "admin") {
    return (
      <>
        <Restricted permissionRequired="frontend_view_appointment_dashboard">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="/appointmentDashboard">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 20)}
                selected={selectedIndex === 20}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("appointment_dashboard")}
                  icon="appointmentDashboard"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        <Restricted permissionRequired="frontend_view_appointment_request">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="/appointmentrequest">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 21)}
                selected={selectedIndex === 21}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("appointmentrequest_single")}
                  icon="newAppointment"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        <Restricted permissionRequired="frontend_view_appointment">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="/appointmentList">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 26)}
                selected={selectedIndex === 26}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("appointments_booked")}
                  icon="bookedAppointments"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        <Restricted permissionRequired="frontend_view_accidentbook">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="/accidentBook">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 15)}
                selected={selectedIndex === 15}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("accidentbook")}
                  icon="accidentbook"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        <Restricted permissionRequired="frontend_view_preventive_care">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="/preventiveCareList">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 14)}
                selected={selectedIndex === 14}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("preventive_care")}
                  icon="preventiveCare"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        <Restricted permissionRequired="frontend_view_subject_company_overview">
          <ListItem
            disablePadding
            className={drawerOpen ? "navigationItemOpen" : "navigationItem"}
          >
            <NavigationNavLink to="/subjectOverview">
              <NavigationItemButton
                drawerOpen={drawerOpen}
                onClick={(event: any) => handleListItemClick(event, 42)}
                selected={selectedIndex === 42}
              >
                <NavigationListItemContent
                  drawerOpen={drawerOpen}
                  primaryText={t("subject_company_overview")}
                  icon="subjectCompanyOverview"
                />
              </NavigationItemButton>
            </NavigationNavLink>
          </ListItem>
        </Restricted>
        {lmsNavigation}
        {/* ---------------------------- Admin CollapseSubNavigation ---------------------------- */}
        {hasAnyAccessibleSubRoutes(adminSubroutes) && (
          <>
            <GenericExpandableNavigationListItem
              listItemIconName="admin"
              listItemLabel={t("admin")}
              drawerOpen={drawerOpen}
              handleSubrouteOpen={toggleSubrouteOpen}
              subRouteOpen={subroutesOpen}
              subRouteKey="admin"
            />
            <GenericNavigationCollapse
              routesToRender={adminSubroutes}
              drawerOpen={drawerOpen}
              selectedIndex={selectedIndex}
              subRouteOpen={subroutesOpen}
              handleListSoubrouteItemClick={handleListSoubrouteItemClick}
              subRouteKey="admin"
            />
          </>
        )}
        {/* ---------------------------- TrackedService CollapseSubNavigation ---------------------------- */}
        {hasAnyAccessibleSubRoutes(trackedServiceSubRoutes) && (
          <>
            <GenericExpandableNavigationListItem
              listItemIconName="admin"
              listItemLabel={t("trackedservice")}
              drawerOpen={drawerOpen}
              handleSubrouteOpen={toggleSubrouteOpen}
              subRouteOpen={subroutesOpen}
              subRouteKey="trackedservice"
            />
            <GenericNavigationCollapse
              routesToRender={trackedServiceSubRoutes}
              drawerOpen={drawerOpen}
              selectedIndex={selectedIndex}
              subRouteOpen={subroutesOpen}
              handleListSoubrouteItemClick={handleListSoubrouteItemClick}
              subRouteKey="trackedservice"
            />
          </>
        )}
        {/* ---------------------------- Settings CollapseSubNavigation ---------------------------- */}
        {hasAnyAccessibleSubRoutes(settingsSoubRoutes) && (
          <>
            <GenericExpandableNavigationListItem
              listItemIconName="admin"
              listItemLabel={t("settings")}
              drawerOpen={drawerOpen}
              handleSubrouteOpen={toggleSubrouteOpen}
              subRouteOpen={subroutesOpen}
              subRouteKey="settings"
            />
            <GenericNavigationCollapse
              routesToRender={settingsSoubRoutes}
              drawerOpen={drawerOpen}
              selectedIndex={selectedIndex}
              subRouteOpen={subroutesOpen}
              handleListSoubrouteItemClick={handleListSoubrouteItemClick}
              subRouteKey="settings"
            />
          </>
        )}
      </>
    );
  }

  if (subdomain === "portal") {
    return isAllowedTo(COURSE_ACCESS_PERMISSION) && canAccessLms ? (
      <>{portalLmsNavigation}</>
    ) : (
      <></>
    );
  }

  return <></>;
};

export default AdminRoutes;
